// Shared helpers for the GameDetail page. These map analytics trade rows
// onto a single team's yes-axis so the chart can plot Kalshi (which has
// two tickers per game, one per team) and Polymarket (which has one
// ticker per game, named after one team) consistently.

import type { TradeRow } from '@/services/hydraAnalytics';
import { kalshiTotalLineFromStrike } from '@/lib/kalshiTicker/totalLine';

export type Venue = 'kalshi' | 'polymarket';

// Extract the canonical team code a ticker is named after — i.e. the team
// whose win resolves YES. Returns one of the two canonical team codes
// (matching `bothCanonicalTeams`) or null when the shape isn't recognized.
//
// Why we need the canonical pair: venues use different short codes for the
// same team (e.g. Kalshi "MTL" vs Polymarket "MON"). Hydra normalizes the
// `game_key` to alpha-sorted canonical codes, but the underlying `ticker`
// preserves the venue's local code. We map back by either direct match or
// by elimination — moneyline always has exactly two outcomes, so a single
// matching code on the OTHER position implies the named code is the alias
// for the remaining canonical team.
//
// Kalshi moneyline ticker shapes:
//   KX{SPORT}GAME-{YY}{MMM}{DD}{HHMM?}{T1}{T2}-{OUTCOME}
//   The trailing '-' segment is the OUTCOME (YES) team code.
//
// Polymarket moneyline ticker shape (per stores/marketStore parsePolyTicker):
//   PM-{sport}-{teamA}-{teamB}-{YYYY-MM-DD}{optional-suffix}
//   teamA is the "named" / YES team by Polymarket convention.
export function tickerNamedTeam(
  ticker: string,
  source: string,
  bothCanonicalTeams: readonly [string, string],
): string | null {
  if (!ticker) return null;
  const a = bothCanonicalTeams[0].toUpperCase();
  const b = bothCanonicalTeams[1].toUpperCase();
  const src = (source ?? '').toLowerCase();

  if (src === 'polymarket' || ticker.startsWith('PM-')) {
    const m = ticker.match(
      /^PM-[a-z]+-([a-z0-9]+)-([a-z0-9]+)-\d{4}-\d{2}-\d{2}/,
    );
    if (!m) return null;
    const named = m[1].toUpperCase();
    const other = m[2].toUpperCase();
    if (named === a || named === b) return named;
    // Alias collapse: if the OTHER slot matches a canonical team, the
    // named slot must be the alias for the remaining canonical team.
    if (other === a) return b;
    if (other === b) return a;
    return null;
  }

  // Kalshi — last segment after the final '-' is the YES-team code.
  const last = ticker.split('-').pop();
  if (!last) return null;
  const upper = last.toUpperCase();
  if (upper === a || upper === b) return upper;
  return null;
}

export interface TeamProjection {
  /** Side from the perspective of the selected team: was this trade
   *  expressing 'selected team wins' (yes) or 'selected team doesn't win' (no)? */
  effectiveSide: 'yes' | 'no';
  /** Executed price in cents — unchanged from the raw row. The implied
   *  yes-price for the selected team is `effectiveSide==='yes' ? price : 100-price`. */
  priceCents: number;
  /** Original venue, for color coding. */
  venue: Venue;
  /** Original raw row — kept so the trades table can show full attribution. */
  row: TradeRow;
}

// Project a raw trade row onto the selected team's yes-axis. Returns null
// when the row doesn't belong to either of the two game teams (defensive —
// shouldn't happen for a correctly-filtered `getTrades` call, but the
// canonical gameKey can collide across sports in rare cases).
export function projectTradeForTeam(
  row: TradeRow,
  selectedTeam: string,
  bothTeams: readonly [string, string],
): TeamProjection | null {
  const named = tickerNamedTeam(row.ticker, row.source, bothTeams);
  if (!named) return null;
  const rawSide = (row.side ?? '').toLowerCase();
  if (rawSide !== 'yes' && rawSide !== 'no') return null;

  const sameTeam = named === selectedTeam.toUpperCase();
  // When the ticker is named after the OTHER team, swap the side label so
  // the bubble is plotted in the bucket that matches the selected-team
  // viewpoint. The trade price doesn't change — the price someone paid
  // for YES-other-team is identically the implied price for NO-selected.
  const effectiveSide: 'yes' | 'no' = sameTeam
    ? (rawSide as 'yes' | 'no')
    : rawSide === 'yes'
      ? 'no'
      : 'yes';

  const venue: Venue =
    (row.source ?? '').toLowerCase() === 'polymarket' ? 'polymarket' : 'kalshi';

  return {
    effectiveSide,
    priceCents: row.price,
    venue,
    row,
  };
}

// USD-at-risk on a single trade, using hydra's convention that 1 contract
// (Kalshi) or 1 share (Polymarket) pays $1 at settlement. Same formula for
// both venues — no per-venue size normalization needed.
export function tradeUsd(row: TradeRow): number {
  return (row.price * row.size) / 100;
}

// Extract the event start time (epoch ms) from a Kalshi ticker. MLB/NBA/
// WNBA per-day game tickers encode the start time as `{YY}{MMM}{DD}{HHMM}`
// in Eastern Time (Kalshi's reference timezone). Tickers without an
// embedded time (NHL, MLB pre-time-encoding) return null. The fallback
// when no ticker yields a start time is on the caller.
const MONTHS: Record<string, number> = {
  JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5,
  JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11,
};

export function eventStartFromKalshiTicker(ticker: string): number | null {
  // KX{SPORT}GAME-{YY}{MMM}{DD}{HHMM}{TEAMS}-{OUTCOME}
  //               └──── 7 chars ───┘└─── 4 chars ───┘
  const m = ticker.match(/^KX[A-Z]+GAME-(\d{2})([A-Z]{3})(\d{2})(\d{4})[A-Z]/);
  if (!m) return null;
  const [, yy, mmm, dd, hhmm] = m;
  const month = MONTHS[mmm];
  if (month == null) return null;
  const year = 2000 + Number(yy);
  const day = Number(dd);
  const hh = Number(hhmm.slice(0, 2));
  const mm = Number(hhmm.slice(2, 4));
  // Build the date in ET. Standard hack: assume EDT (UTC-4) during the
  // sports season (Mar-Nov). Off by an hour for a few weeks each year
  // but close enough to anchor the chart's left edge.
  const utcMs = Date.UTC(year, month, day, hh + 4, mm);
  return Number.isFinite(utcMs) ? utcMs : null;
}

// ── Spread/total line parsing ─────────────────────────────────────
//
// Best-effort line-value extraction from a Kalshi or Polymarket
// spread/total ticker string. Used as a fallback when marketStore
// doesn't have a live HydraMarket frame for the ticker (archived
// games, hydra subscription gap, etc) — without this the line chip
// and the trades-table Bet column fall back to raw ticker strings.
//
// Conventions match marketStore.getGameEvents's derivation:
//   • Kalshi spread outcome = "{TEAM}{BUCKET}" → line = bucket − 0.5
//   • Kalshi total outcome = "{STRIKE}" → line = strike − 0.5 for
//     MLB + WNBA ("minimum total" naming convention), strike + 0.5
//     for everyone else ("line floor" convention).
//   • Polymarket spread slug = "-spread-(home|away)-{NpN}" where pN
//     is the decimal stand-in (`pt` → `.`).
//   • Polymarket total slug = "-total-{NpN}".

export interface LineInfo {
  marketType: 'spread' | 'total';
  magnitude: number;
  /** Spread-only: which side covers when YES resolves. Derived from
   *  Kalshi outcome team-code position (away vs home in the packed
   *  teams chunk) or Polymarket slug side. Carries cross-venue so a
   *  Kalshi "DET covers" line can be grouped with the matching Poly
   *  "spread-away-X.5" line on the same chip. Null for totals and for
   *  Kalshi spreads where the team-position lookup fails. */
  favoredSide: 'away' | 'home' | null;
  /** Spread-only: 3-letter team code from the Kalshi outcome. Used
   *  for chip labels ("BOS 1.5"). Null for totals and Polymarket
   *  spreads (their slug only encodes home/away, not the team code). */
  favoredTeam: string | null;
}



export function lineInfoFromTicker(ticker: string): LineInfo | null {
  // Kalshi spread: KX{SPORT}SPREAD-{DATE}[{HHMM}]{TEAMS}-{TEAM}{BUCKET}
  // Date is 7 chars (YYMMMDD); optional 4-char HHMM block after it; then
  // packed team codes; then the final outcome.
  const ksp = ticker.match(
    /^KX[A-Z]+SPREAD-(\d{2}[A-Z]{3}\d{2}(?:\d{4})?)([A-Z]+)-([A-Z]+)(\d+)$/,
  );
  if (ksp) {
    const teamsPart = ksp[2];
    const favoredTeam = ksp[3];
    const bucket = Number(ksp[4]);
    if (Number.isFinite(bucket) && bucket > 0) {
      // The packed teamsPart is "{awayCode}{homeCode}" — match the
      // favored team to one end. startsWith → away, endsWith → home.
      // Both-match (rare with substring codes) defaults to away.
      let favoredSide: 'away' | 'home' | null = null;
      if (teamsPart.startsWith(favoredTeam)) favoredSide = 'away';
      else if (teamsPart.endsWith(favoredTeam)) favoredSide = 'home';
      return {
        marketType: 'spread',
        magnitude: bucket - 0.5,
        favoredSide,
        favoredTeam,
      };
    }
  }
  // Kalshi total: KX{SPORT}TOTAL-{DATE+TEAMS}-{STRIKE}
  const kt = ticker.match(/^KX([A-Z]+)TOTAL-[^-]+-(\d+)$/);
  if (kt) {
    const strike = Number(kt[2]);
    if (Number.isFinite(strike)) {
      return {
        marketType: 'total',
        magnitude: kalshiTotalLineFromStrike(ticker, strike),
        favoredSide: null,
        favoredTeam: null,
      };
    }
  }
  // Polymarket spread: ...-spread-(home|away)-{N}pt{N}
  const psp = ticker.match(/-spread-(home|away)-(\d+(?:pt\d+)?)/);
  if (psp) {
    const line = Number(psp[2].replace('pt', '.'));
    if (Number.isFinite(line)) {
      return {
        marketType: 'spread',
        magnitude: line,
        favoredSide: psp[1] as 'away' | 'home',
        favoredTeam: null,
      };
    }
  }
  // Polymarket total: ...-total-{N}pt{N}
  const ptot = ticker.match(/-total-(\d+(?:pt\d+)?)/);
  if (ptot) {
    const line = Number(ptot[1].replace('pt', '.'));
    if (Number.isFinite(line)) {
      return {
        marketType: 'total',
        magnitude: line,
        favoredSide: null,
        favoredTeam: null,
      };
    }
  }
  return null;
}

// Find the earliest event start time across every trade in the set.
// Walks the trades once. Returns null when no Kalshi ticker yielded a
// start time — caller decides the fallback (last-N-hours, etc.).
export function earliestEventStart(trades: TradeRow[]): number | null {
  let earliest: number | null = null;
  for (const row of trades) {
    if ((row.source ?? '').toLowerCase() !== 'kalshi') continue;
    const t = eventStartFromKalshiTicker(row.ticker);
    if (t != null && (earliest == null || t < earliest)) {
      earliest = t;
    }
  }
  return earliest;
}
